"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Fragment, useEffect, useRef, useState } from "react";

import { useTick } from "@/hooks/useTick";

import { LogoOI } from "@/components/common/LogoOI";
import { ThemeToggle } from "@/components/header/ThemeToggle";
import { AuthMenu } from "@/components/header/AuthMenu";
import {
  DESCRIZIONE_SITO,
  EDITORIALI_PATH,
  RULLO_HREF,
  RULLO_PRIORITIES,
} from "@/lib/constants";
import { RULLO_PATH } from "@/lib/rullo";
import { CORNICE_HOME } from "@/components/home/stile";

type NavLinkProps = {
  href: string;
  label: string;
  active: boolean;
  onClick?: () => void;
};

/** Le voci della fascia, dai disegni del 15/10/2026 (testata identica in
 *  `Main.dc.html`, `Notizia.dc.html` e `NotiziaChiusa.dc.html`): Geist 14
 *  a peso 500, in tondo e non in maiuscolo, padding 13 sopra e sotto. Il
 *  filetto di due pixel al piede ce l'hanno tutte, trasparente sulle
 *  spente: nel disegno ce l'ha solo l'accesa, e la fila (che è un flex
 *  stirato) prende comunque l'altezza di quella; così l'altezza resta la
 *  stessa anche sulle rotte dove nessuna voce è accesa.
 *
 *  Le interlinee sono in pixel e non `normal`: il disegno le lascia a
 *  `normal`, ma il Geist servito da `next/font` ha metriche più alte di
 *  quello del disegno (14px: 21 contro 18), e la fascia usciva alta 49
 *  invece di 46. Misurate sul disegno: 18 a 14px, 17 a 13px. */
const VOCE_FASCIA =
  "border-b-2 py-[13px] font-sans text-[14px] font-medium leading-[18px] tracking-normal";

/** Una voce della fascia delle sezioni: solo la parola, senza simbolo
 *  davanti (i simboli restano nel pannello del telefono). Spenta nel
 *  tono chiaro secondario della fascia (#bcd3d8, 6,25:1 sul mare; al buio
 *  #9fb0b6, 8,39:1), accesa nel tono pieno (8,63:1 e 15,80:1) con il
 *  filetto di due pixel nel segnale al piede. Fino al 15/10/2026 la
 *  fascia era chiara, con le voci maiuscole in monospazio e la voce
 *  accesa in sabbia: è nella storia di git. */
function NavLink({ href, label, active, onClick }: NavLinkProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`inline-flex items-center whitespace-nowrap transition-colors ${VOCE_FASCIA} ${FUOCO_FASCIA} ${
        active
          ? "border-segnale text-suFasciaMare"
          : "border-transparent text-suFasciaMare2 hover:text-suFasciaMare"
      }`}
    >
      {label}
    </Link>
  );
}

/** Il contorno del fuoco da tastiera.
 *
 *  Scritto a mano perché il contorno di serie del browser è pensato per
 *  la carta chiara: sulla fascia navy il suo blu scuro spariva, e una
 *  voce raggiunta col tasto Tab non si distingueva dalle altre. Sulla
 *  fascia il tono è `navbarGlow` (6,58:1 sul fondo), sulla carta
 *  l'accento. Il contorno sta **dentro** la voce (`outline-offset`
 *  negativo): le voci della fascia sono alte quanto la fascia, e un
 *  contorno esterno finirebbe tagliato sopra e sotto. */
const FUOCO_FASCIA =
  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-suFasciaMare";
const FUOCO_CARTA =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

/** Le dodici destinazioni della testata sono queste undici più
 *  l'accoglienza, che non è una sezione e ha il suo bottone.
 *
 *  L'ordine è la gerarchia: prima le sette voci che stanno in fila nella
 *  fascia, cioè le sezioni del giornale e la strada per scrivergli (è la
 *  fila del bozzetto, uploads/Homepage_rifinita_OI.html), poi le cinque
 *  che stanno dietro «Approfondimenti». Il pannello del telefono rende
 *  lo stesso elenco nello stesso ordine, con il gruppo staccato da
 *  un'intestazione. */
const NAV: Array<{
  href: string;
  symbol: string;
  label: string;
  /** Rotte sotto cui la voce resta accesa. Una o più. */
  prefix?: string | string[];
}> = [
  // La prima voce è la prima pagina. Il marchio ci porta già, e
  // continua a farlo, ma un marchio è un segno che si scopre: chi
  // legge un dispaccio e vuole tornare all'edizione cerca una parola
  // nella barra, non un logo.
  { href: "/", symbol: "◇", label: "Prima pagina" },
  // Il rullo, cioè le notizie: tutti i tipi tranne l'archivio storico.
  // RULLO_HREF è /rullo, cioè i ~1.500 dispacci veri e non /ricerca
  // nuda, che sono 103 mila di cui 101.818 d'archivio fermo. Acceso
  // anche su /ricerca, dove atterrano i link per anno di /archivio.
  { href: RULLO_HREF, symbol: "◈", label: "Rullo", prefix: [RULLO_PATH, "/ricerca"] },
  // I pezzi firmati.
  {
    href: EDITORIALI_PATH,
    symbol: "◉",
    label: "Editoriali",
    /* Accesa anche sulla pagina di lettura (/editoriali/[id]/[slug]). */
    prefix: EDITORIALI_PATH,
  },
  // Il prodotto della casa, subito dopo il rullo e gli editoriali.
  { href: "/report", symbol: "▤", label: "Report" },
  // La voce «Regioni» (/regioni, con l'indice dei paesi) è uscita dalla
  // barra il 19/10/2026 su richiesta del committente. Le pagine restano
  // online e raggiungibili dai rimandi interni (prima pagina, schede paese,
  // /archivio): è tolta solo la voce.
  { href: "/servizi", symbol: "⬡", label: "Servizi", prefix: "/servizi" },
  // L'unica voce che chiede qualcosa al lettore. Chiude la fila perché è
  // la fine del percorso, non una sezione da leggere. Il glifo è della
  // stessa famiglia geometrica degli altri (▣ come il ▤ dei report): la
  // busta ✉ la disegnerebbe il ripiego di sistema.
  { href: "/contatti", symbol: "▣", label: "Contatti", prefix: ["/contatti", "/nda"] },
  // Le cinque voci di «Approfondimenti» (vedi APPROFONDIMENTI qui sotto).
  { href: "/chi-siamo", symbol: "✦", label: "Chi siamo" },
  { href: "/cibernetica", symbol: "◆", label: "Cibernetica" },
  { href: "/osint", symbol: "⊙", label: "OSINT" },
  { href: "/ai", symbol: "⬢", label: "AI" },
  { href: "/lavagna", symbol: "◎", label: "Lavagna" },
];

/** Le rotte che nella fascia stanno dietro un pulsante solo.
 *
 *  La fila è l'indice del giornale: che cosa è successo (rullo,
 *  editoriali, report), che cosa la casa vende (servizi) e come
 *  la si raggiunge (contatti). Le cinque qui sotto raccontano chi siamo e
 *  come lavoriamo — la casa, la cibernetica, il metodo OSINT, l'AI, la
 *  lavagna — e sono pagine che si aprono una volta, non tutti i giorni.
 *  Dietro un pulsante restano raggiungibili in un clic, e la fila scende
 *  da dieci voci a sei più il pulsante: a 1024, dove prima restavano
 *  ventidue pixel, ora ce n'è abbastanza da allargare il passo.
 *
 *  Nel pannello del telefono le cinque voci restano piatte, sotto
 *  un'intestazione: là ogni voce ha una riga sua, e una tendina dentro
 *  un pannello a tendina sarebbe un secondo scalino per niente. */
const APPROFONDIMENTI = ["/chi-siamo", "/cibernetica", "/osint", "/ai", "/lavagna"];

/** Dopo quale voce della fila si apre il pulsante «Approfondimenti».
 *
 *  Scritto come rotta e non come indice: l'ordine di NAV cambia, e un
 *  numero scritto a mano si sposterebbe in silenzio sulla voce
 *  sbagliata. Va dopo SERVIZI, cioè in coda alle sezioni e prima di
 *  CONTATTI, che chiude la riga. */
const TENDINA_DOPO = "/servizi";

/** Il pulsante «Approfondimenti» e la sua tendina.
 *
 *  Vive solo nella fascia delle sezioni, quindi da `lg` in su, ed è
 *  vestito come le voci accanto: stesso corpo, stessa spaziatura, stesso
 *  filetto al piede quando è acceso. Un pulsante che si vestisse da
 *  bottone in mezzo a dieci parole si leggerebbe come un'azione, e non è
 *  un'azione: è una voce di indice che ne contiene tre.
 *
 *  I toni sono quelli della fascia, e vanno scritti come valore
 *  arbitrario — `bg-[var(--color-navbar)]`, non `bg-navbar`: le cinque
 *  variabili stanno nel `:root` e nel `.dark` ma non nel blocco
 *  `@theme`, quindi l'utility non esiste e il colore ricadrebbe in
 *  silenzio su quello ereditato. È la forma di tutto il resto del file.
 *
 *  Il pannello non si chiude al clic su una voce. Chiuderlo lì vuol dire
 *  smontarlo nello stesso giro dell'evento in cui `<Link>` deve
 *  navigare; lo chiude il cambio di rotta, che è la stessa strada del
 *  pannello del telefono (l'effetto su `pathname` in <Header>). */
function TendinaApprofondimenti({
  voci,
  attiva,
  aperta,
  setAperta,
  isActive,
}: {
  voci: typeof NAV;
  /** Una delle rotte raggruppate è quella corrente. */
  attiva: boolean;
  aperta: boolean;
  setAperta: (v: boolean) => void;
  isActive: (href: string, prefix?: string | string[]) => boolean;
}) {
  const scatola = useRef<HTMLDivElement>(null);
  const bottone = useRef<HTMLButtonElement>(null);

  // Chiude con Esc e con un clic fuori. L'Esc riporta il fuoco al
  // pulsante: chi ha aperto la tendina da tastiera, chiudendola, deve
  // ritrovarsi dove stava e non in cima al documento.
  useEffect(() => {
    if (!aperta) return;
    const suTasto = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setAperta(false);
        bottone.current?.focus();
      }
    };
    const suClic = (e: MouseEvent) => {
      if (!scatola.current?.contains(e.target as Node)) setAperta(false);
    };
    document.addEventListener("keydown", suTasto);
    document.addEventListener("mousedown", suClic);
    return () => {
      document.removeEventListener("keydown", suTasto);
      document.removeEventListener("mousedown", suClic);
    };
  }, [aperta, setAperta]);

  /* Le frecce scorrono le voci, come in un menu. `preventDefault` sulle
     due verticali, altrimenti la pagina scorre sotto il pannello. */
  const suFrecce = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const righe = [
      ...(scatola.current?.querySelectorAll<HTMLAnchorElement>("[data-voce]") ??
        []),
    ];
    if (righe.length === 0) return;
    const i = righe.indexOf(document.activeElement as HTMLAnchorElement);
    const passo = e.key === "ArrowDown" ? 1 : -1;
    const prossimo = i < 0 ? 0 : (i + passo + righe.length) % righe.length;
    righe[prossimo]?.focus();
  };

  return (
    <div ref={scatola} className="relative flex items-stretch" onKeyDown={suFrecce}>
      <button
        ref={bottone}
        type="button"
        aria-expanded={aperta}
        aria-controls="oi-tendina-approfondimenti"
        onClick={() => setAperta(!aperta)}
        onKeyDown={(e) => {
          // La freccia in giù apre e porta sulla prima voce, che è come
          // si aprono i menu da tastiera dappertutto.
          if (e.key === "ArrowDown" && !aperta) setAperta(true);
        }}
        className={`inline-flex items-center whitespace-nowrap transition-colors ${VOCE_FASCIA} ${FUOCO_FASCIA} ${
          attiva
            ? "border-segnale text-suFasciaMare"
            : "border-transparent text-suFasciaMare2 hover:text-suFasciaMare"
        }`}
      >
        Approfondimenti
        {/* Nessuna freccina accanto: nel disegno «Approfondimenti» è una
            parola come le altre, e la freccina allargava la voce di 19
            pixel spostando tutta la fila. Che apra qualcosa lo dicono
            `aria-expanded` e il pannello. */}
      </button>

      {aperta ? (
        <div
          id="oi-tendina-approfondimenti"
          className="absolute left-0 top-full z-50 min-w-[210px] bg-fasciaMare py-1.5 shadow-[0_18px_30px_rgba(0,0,0,0.28)]"
        >
          {voci.map((v) => {
            const acceso = isActive(v.href, v.prefix);
            return (
              <Link
                key={v.href}
                href={v.href}
                data-voce
                aria-current={acceso ? "page" : undefined}
                /* Corpo e spaziatura seguono la fila qui accanto: 12
                   pixel con 0,06em, che in monospazio costa per
                   carattere esattamente quanto gli 11 pixel con 0,12em
                   di prima (12 × 0,66 = 11 × 0,72 = 7,92). Il pannello
                   non cambia larghezza, le parole si leggono meglio. */
                /* La tendina parla la lingua della fila: Geist 14 a peso
                   500 sul mare, la voce accesa nel tono pieno con il
                   filetto del segnale a sinistra. */
                className={`flex items-center border-l-2 px-4 py-2.5 font-sans text-[14px] font-medium leading-normal tracking-normal no-underline transition-colors ${FUOCO_FASCIA} ${
                  acceso
                    ? "border-segnale text-suFasciaMare"
                    : "border-transparent text-suFasciaMare2 hover:text-suFasciaMare"
                }`}
              >
                {v.label}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** Il centro della riga alta, dai disegni del 15/10/2026: il motto in
 *  Geist 13 a peso 600 nell'inchiostro e, sotto, la data dell'edizione in
 *  JetBrains Mono 12 nella sonda, «Venerdì 9 ottobre 2026 · aggiornato
 *  alle 15:59». Interlinea 1,5, centrato, `flex: 1 1 260px`.
 *
 *  La data dipende dall'orologio di chi legge e il server non la conosce:
 *  passa da `useTick`, che durante il render del server e la prima
 *  idratazione dà `null`. L'ora dell'aggiornamento è quella del dispaccio
 *  più recente del rullo, chiesta al proxy (`/api/cases`, un minuto di
 *  cache condivisa): un orologio che corre direbbe «aggiornato» anche
 *  quando non è uscito niente. Se la richiesta cade, la riga resta la sola
 *  data.
 *
 *  Contro il salto all'idratazione, la riga vera e la più lunga possibile
 *  stanno nella stessa cella di un `inline-grid`, la seconda invisibile:
 *  il gemello già usato in `AuthMenu` e nel `Ticker`. */
const PIU_LUNGA = "Mercoledì 30 settembre 2026 · aggiornato alle 00:00";

function TitoloEdizione() {
  const adesso = useTick(60_000);
  const [ultimo, setUltimo] = useState<number | null>(null);

  useEffect(() => {
    let vivo = true;
    fetch(`/api/cases?pageSize=1&priority=${RULLO_PRIORITIES.join(",")}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { items?: Array<{ time?: number }> } | null) => {
        const t = j?.items?.[0]?.time;
        if (vivo && typeof t === "number") setUltimo(t);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);

  const giorno =
    adesso === null
      ? ""
      : new Date(adesso).toLocaleDateString("it-IT", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "Europe/Rome",
        });
  const ora =
    ultimo === null
      ? ""
      : new Date(ultimo).toLocaleTimeString("it-IT", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Europe/Rome",
        });
  const riga = giorno
    ? `${giorno.charAt(0).toUpperCase()}${giorno.slice(1)}${ora ? ` · aggiornato alle ${ora}` : ""}`
    : "";

  return (
    <p className="m-0 flex-[1_1_260px] text-center leading-[1.5] tracking-normal">
      <span className="font-sans text-[13px] font-semibold text-ink">
        {DESCRIZIONE_SITO.identita}
      </span>
      <br />
      <span className="inline-grid font-mono text-[12px] text-sommesso">
        <span aria-hidden className="invisible col-start-1 row-start-1">
          {PIU_LUNGA}
        </span>
        <span className="col-start-1 row-start-1">{riga}</span>
      </span>
    </p>
  );
}

/** Una riga del pannello del telefono: il simbolo della sezione, la
 *  parola, e il pallino se è la pagina corrente. */
function VocePannello({
  item,
  active,
  onClick,
}: {
  item: (typeof NAV)[number];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Link
      href={item.href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`flex min-h-[44px] items-center gap-3 rounded-sm border px-3 py-2.5 text-[13px] uppercase tracking-[0.14em] transition-colors ${FUOCO_CARTA} ${
        active
          ? "border-accent bg-accentSoft font-bold text-accent"
          : "border-rule text-ink hover:border-accent"
      }`}
    >
      <span aria-hidden className="w-5 text-center text-[16px] text-accent">
        {item.symbol}
      </span>
      <span className="flex-1 font-semibold">{item.label}</span>
      {active ? (
        <span
          aria-hidden
          className="inline-block h-[6px] w-[6px] rounded-full bg-accent"
        />
      ) : null}
    </Link>
  );
}

export function Header() {
  const pathname = usePathname() ?? "";
  /* La testata del riferimento della prima pagina
     (uploads/Homepage_rifinita_OI.html): il marchio e la descrizione su
     una riga senza lastra, sotto la fascia delle sezioni a tutta finestra
     fra due filetti, niente orologio. Dal 30/09/2026 è la testata di
     tutte le rotte; fino a quel giorno la prendevano solo la prima
     pagina, il Rullo e l'indice degli Editoriali, e passando da
     Editoriali a Report cambiavano barra, colori, piede e bordi. La
     variante di prima (fascia indaco, lastra con i fianchi, orologio) è
     stata tolta: è nella storia di git. */
  const [open, setOpen] = useState(false);
  const [tendina, setTendina] = useState(false);

  const isActive = (href: string, prefix?: string | string[]) => {
    if (prefix) {
      const rotte = Array.isArray(prefix) ? prefix : [prefix];
      return rotte.some((r) => pathname === r || pathname.startsWith(`${r}/`));
    }
    return pathname === href;
  };

  // Chiudi il menu mobile e la tendina quando cambia rotta.
  // Per la tendina è anche l'unico modo in cui si chiude cliccando una
  // sua voce: chiuderla nell'`onClick` del <Link> vorrebbe dire
  // smontarla nello stesso giro dell'evento in cui deve navigare.
  useEffect(() => {
    setOpen(false);
    setTendina(false);
  }, [pathname]);

  // Blocca lo scroll del body mentre il menu mobile è aperto
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const accoglienzaActive = isActive("/accoglienza");

  /* La fila della fascia e le tre voci che stanno dietro il pulsante.
     Si ricavano da NAV e non sono un secondo elenco: due liste scritte
     a mano divergono, come è già successo con le tabelle delle
     priorità. Il pannello del telefono continua a rendere NAV per
     intero, tendina compresa. */
  const inTendina = NAV.filter((v) => APPROFONDIMENTI.includes(v.href));
  const inFila = NAV.filter((v) => !APPROFONDIMENTI.includes(v.href));
  const tendinaAttiva = inTendina.some((v) => isActive(v.href, v.prefix));

  return (
    /* La testata dei disegni del 15/10/2026, la stessa nei tre HTML
       consegnati (prima pagina, notizia aperta, notizia chiusa):

       - un filetto di 3 pixel nel rame del segnale in cima, a tutta
         finestra;
       - la riga alta, larga al più 1440 con 32 di rientro e 20 sopra e
         sotto: il marchio (segno 44x35 nel mare col triangolo nel
         segnale, scritta in JetBrains Mono 15 a peso 600), al centro il
         motto e la data, a destra «Chiaro / scuro», «Accedi» e
         «Accoglienza» pieno nel mare;
       - la fascia delle sezioni piena nel mare (`--oi-home-fascia-mare`,
         #0e4a5c nel chiaro, #071318 nello scuro), voci in Geist 14.

       Il disegno è solo da computer: sotto `lg` restano la riga del
       telefono (marchio, accesso, tema, menu) e il pannello a comparsa,
       con i colori nuovi. `tracking-normal` perché il `body` toglie 0,02em
       pensati per il monospazio, e il disegno non toglie niente. */
    <header className="relative border-t-[3px] border-segnale tracking-normal">
      <div className={CORNICE_HOME}>
        <div className="flex min-w-0 items-center justify-between gap-x-3 gap-y-2 py-3 sm:gap-x-6 lg:flex-wrap lg:gap-4 lg:py-5">
          <Link
            href="/"
            aria-label="Osservatorio Italiano, prima pagina"
            className={`flex min-w-0 items-center ${FUOCO_CARTA}`}
          >
            {/* Sotto `sm` il solo segno, alto 35 come nel disegno (32 sotto
                i 360, dove la riga non ha altro posto). */}
            <LogoOI
              variant="segno"
              accento="fill-segnale"
              className="h-8 w-auto shrink-0 text-mare min-[360px]:h-[35px] sm:hidden"
            />
            {/* `sm:flex` e non `sm:block`: il lockup è `inline-flex`, e in
                un blocco si porterebbe dietro l'interlinea del contenitore. */}
            <span className="hidden shrink-0 sm:flex">
              <LogoOI
                variant="lockup"
                tonoSegno="text-mare"
                accento="fill-segnale"
                misuraSegno="h-[35px] w-[44px]"
                vuoto="ml-3"
                className="text-[15px] text-ink"
              />
            </span>
          </Link>

          <div className="hidden lg:contents">
            <TitoloEdizione />
            <div className="flex flex-wrap items-center gap-2">
              <ThemeToggle testata />
              <AuthMenu testata />
              <Link
                href="/accoglienza"
                aria-current={accoglienzaActive ? "page" : undefined}
                /* Il bottone pieno del disegno: Geist 13 a peso 600,
                   padding 13×16, raggio 2, fondo nel mare e testo nel suo
                   contrario (8,63:1 nel chiaro, 7,31:1 nello scuro). */
                className={`inline-flex items-center rounded-[2px] bg-mare px-4 py-[13px] font-sans text-[13px] leading-[17px] font-semibold text-suMare transition-opacity hover:opacity-90 ${FUOCO_CARTA}`}
              >
                Accoglienza&nbsp;<span aria-hidden>↗</span>
              </Link>
            </div>
          </div>

          {/* La riga del telefono e del tablet: accoglienza (da `sm`),
              accesso, tema e menu. */}
          <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2 lg:hidden">
            <Link
              href="/accoglienza"
              aria-label="Accoglienza"
              aria-current={accoglienzaActive ? "page" : undefined}
              /* Pieno nel mare come quello da computer. Sotto `sm` non c'è:
                 a 320 restano quaranta pixel, e l'accoglienza apre il
                 pannello. Sotto `md` alto 44, il bersaglio minimo. */
              className={`hidden min-h-[38px] max-md:min-h-[44px] items-center justify-center rounded-[2px] bg-mare px-[10px] py-2 font-sans text-[12px] leading-normal font-semibold text-suMare sm:inline-flex ${FUOCO_CARTA}`}
            >
              <span>Accoglienza</span>&nbsp;<span aria-hidden>↗</span>
            </Link>
            <AuthMenu />
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Chiudi menu" : "Apri menu"}
              aria-expanded={open}
              aria-controls="oi-mobile-nav"
              /* 44 pixel sul telefono, il bersaglio minimo da toccare; da
                 `md` torna a 36. */
              className={`relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-sm border border-rule bg-surface text-ink transition-colors hover:border-accent md:h-9 md:w-9 ${FUOCO_CARTA}`}
            >
              {open ? (
                <span className="text-[16px] leading-none">✕</span>
              ) : (
                <span aria-hidden className="flex flex-col gap-[3px]">
                  <span className="block h-[1.5px] w-4 bg-ink" />
                  <span className="block h-[1.5px] w-4 bg-ink" />
                  <span className="block h-[1.5px] w-4 bg-ink" />
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* La fascia delle sezioni, solo da `lg`: piena nel mare a tutta
          finestra, voci nella cornice da 1440, distribuite su tutta la
          larghezza (`justify-between`, vuoti di 4 e 24) come nel disegno.
          Niente accesso né tema qui: nel disegno stanno nella riga alta.
          `data-fascia-sezioni`: StickyTopStack ne misura l'altezza. */}
      <div data-fascia-sezioni className="hidden bg-fasciaMare lg:block">
        <div className={CORNICE_HOME}>
          <nav
            aria-label="Sezioni"
            className="flex flex-wrap items-stretch justify-between gap-x-6 gap-y-1"
          >
            {inFila.map((item) => (
              <Fragment key={item.href}>
                <NavLink
                  href={item.href}
                  label={item.label}
                  active={isActive(item.href, item.prefix)}
                />
                {item.href === TENDINA_DOPO ? (
                  <TendinaApprofondimenti
                    voci={inTendina}
                    attiva={tendinaAttiva}
                    aperta={tendina}
                    setAperta={setTendina}
                    isActive={isActive}
                  />
                ) : null}
              </Fragment>
            ))}
          </nav>
        </div>
      </div>

      {/* Pannello mobile a comparsa — slide-down sotto la barra */}
      {open ? (
        <>
          {/* Backdrop dietro il pannello */}
          <button
            type="button"
            aria-label="Chiudi menu"
            onClick={() => setOpen(false)}
            // `top-full` invece del vecchio `top-[60px]` cablato: la
            // barra cambia altezza col breakpoint e coi 40px del
            // bottone menu, e il backdrop restava disallineato.
            className="absolute inset-x-0 top-full z-30 h-screen bg-ink/30 backdrop-blur-sm lg:hidden"
          />
          {/* Pannello con la nav */}
          <div
            id="oi-mobile-nav"
            className="absolute inset-x-0 top-full z-40 origin-top border-b border-rule bg-surface shadow-[0_18px_30px_rgba(0,0,0,0.18)] lg:hidden"
          >
            <nav
              aria-label="Sezioni"
              className="mx-auto flex max-h-[calc(100svh-var(--oi-header-h,72px))] max-w-shell flex-col gap-1.5 overflow-y-auto px-5 py-4 sm:px-7"
            >
              {/* L'accoglienza apre il pannello, staccata dalle sezioni
                  da un filetto: è l'invito della casa, non una sezione
                  del giornale, e sotto `sm` questa è l'unica strada che
                  ha (nella barra non ci sta). Fondo pieno, perché è
                  l'azione, mentre le sezioni qui sotto sono di
                  contorno. */}
              <Link
                href="/accoglienza"
                onClick={() => setOpen(false)}
                aria-current={accoglienzaActive ? "page" : undefined}
                className={`mb-1 flex min-h-[44px] items-center justify-between gap-3 rounded-sm bg-accent px-3 py-2.5 text-[13px] font-bold uppercase tracking-[0.14em] text-onAccent ${FUOCO_CARTA}`}
              >
                <span>Accoglienza</span>
                <span aria-hidden>→</span>
              </Link>
              <span aria-hidden className="mb-2 block h-px w-full bg-rule" />
              {/* Lo stesso elenco della fascia, nello stesso ordine: le
                  voci della fila e, dove la fascia apre «Approfondimenti»
                  (dopo SERVIZI, prima di CONTATTI), il gruppo della
                  tendina, staccato da un'intestazione e non chiuso in una
                  seconda tendina. Fino al 01/10/2026 il gruppo stava in
                  coda, dopo CONTATTI, e passando dal telefono al sito le
                  voci cambiavano ordine. */}
              {inFila.map((item) => (
                <Fragment key={item.href}>
                  <VocePannello
                    item={item}
                    active={isActive(item.href, item.prefix)}
                    onClick={() => setOpen(false)}
                  />
                  {item.href === TENDINA_DOPO ? (
                    <>
                      <p
                        id="oi-pannello-approfondimenti"
                        className="mt-3 mb-1 px-1 text-nota font-bold uppercase tracking-etichetta text-muted"
                      >
                        Approfondimenti
                      </p>
                      <ul
                        aria-labelledby="oi-pannello-approfondimenti"
                        className="mb-3 flex flex-col gap-1.5"
                      >
                        {inTendina.map((voce) => (
                          <li key={voce.href}>
                            <VocePannello
                              item={voce}
                              active={isActive(voce.href, voce.prefix)}
                              onClick={() => setOpen(false)}
                            />
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </Fragment>
              ))}
            </nav>
          </div>
        </>
      ) : null}
    </header>
  );
}
